import { create } from "zustand";

/**
 * Shell-level screen-reader announcer (G28). One shared pair of polite/
 * assertive live regions for the whole app, so dynamic-state components
 * (route changes, connection flips, command completion) announce through a
 * single coordinated seam instead of each inventing its own region. Mirrors
 * useToasts.ts — module-level `announce()` matches `pushToast()`.
 *
 * Screen readers do NOT re-read identical consecutive text, so each setter
 * forces a change by toggling a trailing zero-width space when the message
 * repeats. Text persists harmlessly in the visually-hidden node (no timers).
 */
interface AnnouncerState {
  polite: string;
  assertive: string;
  setPolite(msg: string): void;
  setAssertive(msg: string): void;
}

const ZWSP = "​";
const bump = (next: string, current: string) =>
  next === current.replace(/​+$/, "") ? next + ZWSP : next;

export const useAnnouncer = create<AnnouncerState>((set, get) => ({
  polite: "",
  assertive: "",
  setPolite: (msg) => set({ polite: bump(msg, get().polite) }),
  setAssertive: (msg) => set({ assertive: bump(msg, get().assertive) }),
}));

/** Announce a message to assistive tech. `assertive` interrupts (use sparingly). */
export function announce(message: string, opts?: { assertive?: boolean }): void {
  const s = useAnnouncer.getState();
  if (opts?.assertive) s.setAssertive(message);
  else s.setPolite(message);
}
